// Import react elements
import { useState } from 'react';

// Import global hooks
import useFormHandler from "global/hooks/useFormHandler";
import { useToast } from "global/hooks/useToast";

import useCustomProductsData from "../../hooks/useCustomProductsData";

// Import components
import CreateButton from "global/components/CreateButton";
import TwoColumnList from "global/components/TwoColumnList";

// Import utility functions
import { DisplayConfirmationDialog } from "global/utilities/DialogBox";
import { CloseIcon } from 'global/strich_icons/StrichIcons';

// Render print area details on print area details page
const RenderPrintLayoutPopup = ({
    isOpen, 
    onClose, 
    printLayoutData, 
    handleDeletePrintLayout,
    selectedCustomProduct,
    setSelectedCustomProduct,
    setSelectedPrintLayout,
}) => {


    // Define toast options
    const { toastError } = useToast();


    // Store details
    const [isLoading, setIsLoading] = useState(null);


    // Custom hook for fetching and managing product data
    const {
        updatePrintLayout
    } = useCustomProductsData(); 
    


    // Use the form handler for this single object
    const {

        // Local form values for this specific surface
        formValues,      

        // Local change handler
        handleChange,    

        isFormPartlyEdited,

    } = useFormHandler({

        // Initial values are the surface object passed from the parent
        initialValues: printLayoutData,

        // Sync when `surface` prop changes (e.g. editing a different item)
        syncInitialValues: true,
        convertBooleans: true,
        convertNumbers: true,
        
        // Optional validation - only when using handleSubmit 
        validate: (values) => {

        }

    });

    // Close the print layout editor, warning first if anything has changed
    const handleClosePrintLayout = () => {

        // Close immediately when there is nothing to discard
        if (!isFormPartlyEdited()) {

            onClose();

            return;

        }

        // Confirm before discarding unsaved print layout changes
        DisplayConfirmationDialog(

            "You have unsaved changes. Are you sure you want to close?",
            "Close",
            "Keep Editing",

            async function (is_confirmed) {

                if (is_confirmed) {

                    onClose();

                }

            }

        );

    };

    // Handle local form submit (in it's own file so it doesn't get confused with formValues in parent CustomProduct.js)
    const handleSubmitSavePrintLayout = async (event) => {

        // Set loading state
        setIsLoading(true);

        // Prevent default browser submit behaviour
        event.preventDefault();

        // Copy the editable print layout values
        const printLayoutPayload = { ...formValues };

        // Settings are resolved from the MMM link and are read only here, so do not save them as print layout overrides
        delete printLayoutPayload.settings;

        // Make the call from parent CustomProduct.js
        const response = await updatePrintLayout(selectedCustomProduct.code, printLayoutData.id, printLayoutPayload, setSelectedCustomProduct);
        
        // If parent returns a success flag
        if (response.success) {

            // Make the successfully saved print layout the new editor baseline
            setSelectedPrintLayout(formValues);

        } else {

            // Toast fail message
            toastError({ title: "Error", message: "Could not update Print Layout: " + response.error_message });

        }

        // Clear loading state
        setIsLoading(false);

        // If parent indicates failure, do nothing so the values stay on screen

    };



    // If the pop up isn't open return early
    if (!isOpen) return null;



    return (

        <div className="popup-overlay">
            
            <div className="popup-container popup-container--small" style={{height: "auto"}}>

                {/* This is the modal header */}
                <div className="popup__header">

                    {/* This is the title area */}
                    <div className="popup__headerLeft">

                        {/* This is the parent title */}
                        <h2 className="popup__title">

                            Print Layout

                        </h2>

                        {/* This is the parent subtitle */}
                        <p className="popup__subtitle">

                            {printLayoutData?.production_title} 

                        </p>

                    </div>

                    {/* This is the header actions */}
                    <div className="popup__headerRight">

                        {/* This is the close button */}
                        <button type="button" className="popup__close" onClick={() => handleClosePrintLayout()} title="Close">
                            <CloseIcon />
                        </button>

                    </div>

                </div>


                <div className="popup-content popup-content__body">

                    <div>

                        <form id="printLayoutDetailsForm" onSubmit={handleSubmitSavePrintLayout}>

                            <TwoColumnList 
                                property={<label>Production Title</label>} 
                                value={<input 
                                    name="production_title" 
                                    type="text" 
                                    placeholder="Production Title" 
                                    value={formValues?.production_title || ""}
                                    onChange={handleChange}
                                    required
                                />} 
                            />

                            <TwoColumnList 
                                property={<label>Code</label>} 
                                value={<input 
                                    name="code" 
                                    type="text" 
                                    placeholder="Code"
                                    value={formValues?.code || ""}
                                    onChange={handleChange}
                                    required 
                                    disabled={printLayoutData}
                                />} 
                            />

                            <br/>

                            {formValues.settings && 
                    
                                <>

                                    <p><b>Settings</b></p>
                                    <br/>

                                    <div className="print-area-details__settings">
                                    
                                        {Object.entries(formValues?.settings).map(([key, value]) => {

                                            return (

                                                <div className="settings-list__item" key={key}>

                                                    <span className="settings-list__key">{key}</span><span className="settings-list__value">{value || ""} </span>

                                                </div>

                                            )
                                            
                                        })}

                                    </div>

                                </>
                                                
                            }

                                                       
                            <br/> 

                            <p><b>Selected Mockup</b></p>
                            
                            <div className="print-layout__mockups-select">

                                {selectedCustomProduct.mockups?.map((mockup) => {

                                    return (

                                        <div
                                            key={mockup.id}
                                            className={`print-layout__mockups-image ${formValues.mockup_id === mockup.id ? "print-layout__mockups-image--selected" : ""}`} 
                                            
                                            onClick={() =>
                                                handleChange({
                                                    target: {
                                                        name: "mockup_id",
                                                        value: mockup.id,
                                                    },
                                                })
                                            }
                                            
                                        >

                                            <img src={mockup.mockup_thumbnail_url || mockup.mockup_image_url} alt={mockup.id}></img>

                                        </div>

                                    )

                                })}

                            </div>

                        </form>
                

                        {/* Tab content */}
                        {/* Scrollable content area */}
                        <div className="tabs-scroll-container">

                            <div className="tabs-content">


                            </div>

                        </div>

                        {/* Footer actions */}
                        <div className="tabs-footer tabs-footer--fill-no-background horizontal-align-content-center flex-justify-left-right">

                            <div>

                                {/* Only show delete button if we have positionData, if we don't then they're adding a new one */}
                                {printLayoutData &&
                                
                                    <CreateButton title="Delete Print Layout" isDelete={true} tooltip="Delete Position" size="m" icon="/images/icon_bin.png" onClick={() => handleDeletePrintLayout(printLayoutData)} disabled={isLoading} />

                                }

                            </div>

                            <div className="horizontal-align-content-center">

                                {isFormPartlyEdited() &&

                                    <CreateButton
                                        type="submit"
                                        title="Save changes"
                                        size="m"
                                        formID="printLayoutDetailsForm"
                                        disabled={isLoading}
                                    />

                                }

                                <CreateButton
                                    type="button"
                                    title="Close"
                                    invert
                                    size="m"
                                    onClick={() => handleClosePrintLayout()}
                                    disabled={isLoading}
                                />

                            </div>
                            
                        </div>
                        
                    </div>

                </div>

            </div>

        </div>

    );

}; export default RenderPrintLayoutPopup;
